import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { authAPI, cdlAPI } from '../api';
import { toast } from 'sonner';

/* ── shared paper input styles (mirrors Contact.js) ─────────────────────── */
const paperInput = [
  'bg-transparent border-0 border-b border-[#b8a99a]',
  'py-1 text-[13px] text-[#1a1209] placeholder-[#c4b5a0]',
  'focus:outline-none focus:border-[#7c3aed] transition-colors',
  'font-mono tracking-wide w-full',
].join(' ');

const paperTextarea = [
  'bg-transparent border border-[#d4c9bb] rounded',
  'p-2 text-[13px] text-[#1a1209] placeholder-[#c4b5a0]',
  'focus:outline-none focus:border-[#7c3aed] transition-colors resize-none',
  'font-mono tracking-wide leading-relaxed w-full',
].join(' ');

const PaperField = ({ label, required, children }) => (
  <div className="flex flex-col gap-1">
    <label className="text-[9px] font-black uppercase tracking-[0.18em] text-[#6b5b3e]">
      {label}{required && <span className="text-[#7c3aed] ml-0.5">*</span>}
    </label>
    {children}
  </div>
);

const PaperSection = ({ num, title, children }) => (
  <div className="mb-6">
    <div className="flex items-center gap-2 mb-3">
      <span className="w-5 h-5 rounded-full bg-[#7c3aed] text-white text-[9px] font-black flex items-center justify-center shrink-0">
        {num}
      </span>
      <span className="text-[10px] font-black uppercase tracking-[0.18em] text-[#4a3728]">{title}</span>
      <div className="flex-1 border-t border-dashed border-[#c4b5a0]" />
    </div>
    <div className="space-y-4">{children}</div>
  </div>
);

/* ── status badge colours ────────────────────────────────────────────────── */
const STATUS_META = {
  pending:   { label: 'PENDING REVIEW', color: '#b8860b', bg: '#fef9c3' },
  scheduled: { label: 'SCHEDULED',      color: '#166534', bg: '#dcfce7' },
  completed: { label: 'COMPLETED',      color: '#1e40af', bg: '#dbeafe' },
  closed:    { label: 'CLOSED',         color: '#6b7280', bg: '#f3f4f6' },
};

/* ══════════════════════════════════════════════════════════════════════════ */
const CdlScheduling = () => {
  const [user, setUser]           = useState(null);
  const [authUrl, setAuthUrl]     = useState('');
  const [loading, setLoading]     = useState(true);
  const [existing, setExisting]   = useState(null); // existing CDL request
  const [submitted, setSubmitted] = useState(false);

  const [form, setForm] = useState({
    ic_name: '', ic_phone: '', availability: '',
    preferred_date: '', notes: '',
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('gty_token');
    if (!token) {
      authAPI.getDiscordAuthUrl().then(r => setAuthUrl(r.data.url)).catch(() => {}).finally(() => setLoading(false));
      return;
    }
    authAPI.getUserProfile()
      .then(r => { setUser(r.data); return cdlAPI.my(); })
      .then(r => { setExisting(r.data?.request || null); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const f = (key) => (e) => setForm(p => ({ ...p, [key]: e.target.value }));

  const handleSubmit = async () => {
    if (!form.ic_name.trim() || !form.ic_phone.trim() || !form.availability.trim()) {
      toast.error('Please fill in all required fields.');
      return;
    }
    setSubmitting(true);
    try {
      const r = await cdlAPI.submit(form);
      setExisting(r.data.request);
      setSubmitted(true);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to submit request.';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  /* ─── loading ─────────────────────────────────────────────────────────── */
  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0c] flex items-center justify-center pt-20">
        <motion.div
          className="w-8 h-8 border-2 border-purple-600 border-t-transparent rounded-full"
          animate={{ rotate: 360 }}
          transition={{ duration: 0.8, repeat: Infinity, ease: 'linear' }}
        />
      </div>
    );
  }

  /* ─── not logged in ───────────────────────────────────────────────────── */
  if (!user) {
    return (
      <div className="min-h-screen bg-[#0a0a0c] flex items-center justify-center pt-20 px-4">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center max-w-md"
        >
          <div className="text-5xl mb-4">🪪</div>
          <h2 className="text-2xl font-black uppercase tracking-widest text-white mb-2">
            CDL Scheduling
          </h2>
          <p className="text-[#a1a1aa] text-sm mb-6 leading-relaxed">
            You must be logged in with Discord to submit a CDL scheduling request.
          </p>
          <button
            onClick={() => authUrl && (window.location.href = authUrl)}
            className="btn-primary text-sm px-6 py-3"
          >
            Login with Discord
          </button>
        </motion.div>
      </div>
    );
  }

  /* ─── page wrapper (paper bg) ─────────────────────────────────────────── */
  return (
    <div className="min-h-screen bg-[#0a0a0c] pt-20 pb-16 px-4">

      {/* ── decorative header strip ──────────────────────────────────────── */}
      <div className="max-w-2xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: -12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45 }}
          className="text-center mb-8"
        >
          <p className="text-[9px] font-black uppercase tracking-[0.4em] text-purple-500 mb-1">
            Go Truck Yourself • Official Form
          </p>
          <h1 className="text-3xl md:text-4xl font-black uppercase tracking-tight text-white">
            CDL Scheduling
          </h1>
          <p className="text-xs text-[#71717a] mt-2 tracking-widest uppercase">
            Road Test Appointment Request
          </p>
        </motion.div>

        <AnimatePresence mode="wait">

          {/* ══ EXISTING REQUEST CARD ══════════════════════════════════════ */}
          {existing && !submitted && (
            <motion.div
              key="existing"
              initial={{ opacity: 0, rotateY: 8, scale: 0.97 }}
              animate={{ opacity: 1, rotateY: 0, scale: 1 }}
              exit={{ opacity: 0, scale: 0.96 }}
              transition={{ duration: 0.45, ease: [0.25, 0.46, 0.45, 0.94] }}
              style={{ perspective: 1200 }}
            >
              <PaperCard existingRequest={existing} />
            </motion.div>
          )}

          {/* ══ SUBMIT SUCCESS ════════════════════════════════════════════ */}
          {submitted && existing && (
            <motion.div
              key="success"
              initial={{ opacity: 0, rotateY: 60, scale: 0.96 }}
              animate={{ opacity: 1, rotateY: 0, scale: 1 }}
              transition={{ duration: 0.52, ease: [0.25, 0.46, 0.45, 0.94] }}
              style={{ perspective: 1200 }}
            >
              <PaperCard existingRequest={existing} justSubmitted />
            </motion.div>
          )}

          {/* ══ FORM ══════════════════════════════════════════════════════ */}
          {!existing && !submitted && (
            <motion.div
              key="form"
              initial={{ opacity: 0, rotateY: 60, scale: 0.96 }}
              animate={{ opacity: 1, rotateY: 0, scale: 1 }}
              transition={{ duration: 0.52, ease: [0.25, 0.46, 0.45, 0.94] }}
              style={{ perspective: 1200 }}
            >
              {/* Paper document */}
              <div className="relative bg-[#faf6ef] shadow-[0_8px_40px_rgba(0,0,0,0.55),inset_0_1px_0_rgba(255,255,255,0.6)] rounded-sm overflow-visible">

                {/* Hole punches */}
                {[-1, 0, 1].map(i => (
                  <div
                    key={i}
                    className="absolute -left-4 w-7 h-7 rounded-full bg-[#0a0a0c] shadow-inner border-2 border-[#1a1a1e]"
                    style={{ top: `${50 + i * 20}%`, transform: 'translateY(-50%)' }}
                  />
                ))}

                {/* Corner fold */}
                <div
                  className="absolute top-0 right-0 w-12 h-12 pointer-events-none"
                  style={{
                    background: 'linear-gradient(225deg, #e8e0d4 45%, transparent 45%)',
                    boxShadow: '-2px 2px 4px rgba(0,0,0,0.12)',
                  }}
                />

                <div className="p-8 md:p-10">
                  {/* Document header */}
                  <div className="border-b-2 border-[#4a3728] pb-4 mb-6">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="text-[8px] font-black uppercase tracking-[0.4em] text-[#7c3aed] mb-0.5">
                          Go Truck Yourself — Internal Form
                        </p>
                        <h2 className="text-xl font-black uppercase tracking-tight text-[#1a1209]">
                          CDL Road Test Scheduling Request
                        </h2>
                        <p className="text-[10px] text-[#6b5b3e] mt-0.5 font-mono">
                          Applicant: {user.display_name || user.username}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-[8px] font-mono text-[#9a8575]">REF#</p>
                        <p className="text-[11px] font-black font-mono text-[#4a3728]">
                          CDL-{String(Date.now()).slice(-6)}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Section 1: IC Contact Info */}
                  <PaperSection num="1" title="IC Contact Information">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <PaperField label="IC Full Name" required>
                        <input
                          className={paperInput}
                          placeholder="John A. Doe"
                          value={form.ic_name}
                          onChange={f('ic_name')}
                        />
                      </PaperField>
                      <PaperField label="IC Phone Number" required>
                        <input
                          className={paperInput}
                          placeholder="(555) 000-0000"
                          value={form.ic_phone}
                          onChange={f('ic_phone')}
                        />
                      </PaperField>
                    </div>
                  </PaperSection>

                  {/* Section 2: Scheduling */}
                  <PaperSection num="2" title="Scheduling Preferences">
                    <PaperField label="General Availability" required>
                      <textarea
                        className={paperTextarea}
                        rows={3}
                        placeholder={"e.g. Weekdays after 5pm EST, Weekends anytime\nPlease include your timezone."}
                        value={form.availability}
                        onChange={f('availability')}
                      />
                    </PaperField>
                    <PaperField label="Preferred Date (optional)">
                      <input
                        className={paperInput}
                        type="date"
                        value={form.preferred_date}
                        onChange={f('preferred_date')}
                      />
                    </PaperField>
                  </PaperSection>

                  {/* Section 3: Notes */}
                  <PaperSection num="3" title="Additional Notes">
                    <PaperField label="Notes / Special Requests (optional)">
                      <textarea
                        className={paperTextarea}
                        rows={3}
                        placeholder="Any additional information management should know..."
                        value={form.notes}
                        onChange={f('notes')}
                      />
                    </PaperField>
                  </PaperSection>

                  {/* Declaration line */}
                  <div className="border-t border-dashed border-[#c4b5a0] pt-4 mb-6">
                    <p className="text-[9px] font-mono text-[#9a8575] leading-relaxed">
                      By submitting this form I confirm that the contact information provided is accurate and I understand
                      that GTY management will reach out via the IC phone number listed above around my stated availability.
                    </p>
                  </div>

                  {/* Submit button */}
                  <div className="flex justify-end">
                    <motion.button
                      onClick={handleSubmit}
                      disabled={submitting}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className="relative overflow-hidden bg-[#7c3aed] text-white text-[11px] font-black uppercase tracking-[0.2em] px-8 py-3 disabled:opacity-60 transition-all shadow-lg"
                    >
                      {submitting ? (
                        <span className="flex items-center gap-2">
                          <motion.span
                            className="w-3 h-3 border border-white border-t-transparent rounded-full"
                            animate={{ rotate: 360 }}
                            transition={{ duration: 0.7, repeat: Infinity, ease: 'linear' }}
                          />
                          Submitting...
                        </span>
                      ) : (
                        'Submit Request'
                      )}
                    </motion.button>
                  </div>
                </div>
              </div>
            </motion.div>
          )}

        </AnimatePresence>
      </div>
    </div>
  );
};

/* ══ Paper card (existing request / success) ════════════════════════════════ */
const PaperCard = ({ existingRequest: req, justSubmitted = false }) => {
  const meta = STATUS_META[req.status] || STATUS_META.pending;
  const fmtDate = (iso) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  return (
    <div className="relative bg-[#faf6ef] shadow-[0_8px_40px_rgba(0,0,0,0.55),inset_0_1px_0_rgba(255,255,255,0.6)] rounded-sm overflow-visible">

      {/* Hole punches */}
      {[-1, 0, 1].map(i => (
        <div
          key={i}
          className="absolute -left-4 w-7 h-7 rounded-full bg-[#0a0a0c] shadow-inner border-2 border-[#1a1a1e]"
          style={{ top: `${50 + i * 20}%`, transform: 'translateY(-50%)' }}
        />
      ))}

      {/* Corner fold */}
      <div
        className="absolute top-0 right-0 w-12 h-12 pointer-events-none"
        style={{
          background: 'linear-gradient(225deg, #e8e0d4 45%, transparent 45%)',
          boxShadow: '-2px 2px 4px rgba(0,0,0,0.12)',
        }}
      />

      <div className="p-8 md:p-10">
        {/* Header */}
        <div className="border-b-2 border-[#4a3728] pb-4 mb-6">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <p className="text-[8px] font-black uppercase tracking-[0.4em] text-[#7c3aed] mb-0.5">
                Go Truck Yourself — CDL Request
              </p>
              <h2 className="text-xl font-black uppercase tracking-tight text-[#1a1209]">
                Road Test Scheduling
              </h2>
              <p className="text-[10px] text-[#6b5b3e] mt-0.5 font-mono">
                Submitted: {fmtDate(req.created_at)}
              </p>
            </div>
            {/* Status stamp */}
            <div className="flex flex-col items-end gap-1">
              <div
                className="px-3 py-1 border-2 font-black text-[10px] uppercase tracking-[0.2em] rotate-[-2deg]"
                style={{ color: meta.color, borderColor: meta.color, background: meta.bg }}
              >
                {meta.label}
              </div>
              {justSubmitted && (
                <motion.div
                  initial={{ scale: 2, opacity: 0, rotate: -15 }}
                  animate={{ scale: 1, opacity: 1, rotate: -3 }}
                  transition={{ delay: 0.3, duration: 0.5, type: 'spring', bounce: 0.35 }}
                  className="text-[9px] font-black uppercase tracking-widest text-[#7c3aed] border border-[#7c3aed] px-2 py-0.5"
                >
                  SUBMITTED
                </motion.div>
              )}
            </div>
          </div>
        </div>

        {/* Fields */}
        <div className="space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <ReadonlyField label="IC Full Name"   value={req.ic_name} />
            <ReadonlyField label="IC Phone"       value={req.ic_phone} />
          </div>
          <ReadonlyField label="Availability"     value={req.availability} multiline />
          {req.preferred_date && (
            <ReadonlyField label="Preferred Date" value={req.preferred_date} />
          )}
          {req.notes && (
            <ReadonlyField label="Notes"          value={req.notes} multiline />
          )}
          {req.management_note && (
            <div className="border-t border-dashed border-[#c4b5a0] pt-4">
              <ReadonlyField label="Management Note" value={req.management_note} multiline highlight />
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-dashed border-[#c4b5a0] pt-4 mt-6">
          <p className="text-[9px] font-mono text-[#9a8575] leading-relaxed">
            {req.status === 'pending'
              ? 'Your request is under review. GTY management will reach out via your IC phone number around your listed availability.'
              : req.status === 'scheduled'
              ? `Your road test has been scheduled. Expect a call on ${req.ic_phone} around your listed availability.`
              : req.status === 'completed'
              ? 'This CDL scheduling request has been completed.'
              : 'This request has been closed.'}
          </p>
        </div>
      </div>
    </div>
  );
};

const ReadonlyField = ({ label, value, multiline = false, highlight = false }) => (
  <div className="flex flex-col gap-1">
    <span className="text-[9px] font-black uppercase tracking-[0.18em] text-[#6b5b3e]">{label}</span>
    {multiline ? (
      <p
        className={`text-[13px] font-mono tracking-wide leading-relaxed border border-[#d4c9bb] rounded p-2 whitespace-pre-wrap ${
          highlight ? 'bg-[#fef9c3] text-[#1a1209] border-[#b8860b]' : 'text-[#1a1209]'
        }`}
      >
        {value}
      </p>
    ) : (
      <p className="text-[13px] font-mono tracking-wide text-[#1a1209] border-b border-[#d4c9bb] pb-1">
        {value || '—'}
      </p>
    )}
  </div>
);

export default CdlScheduling;
